/**
 * SimulatorPanel - admin/dev tool to create CPU leagues and fast-complete drafts.
 *
 * Gated by REACT_APP_SIMULATOR_ENABLED OR User.role == "admin".
 * Visible at /simulator.
 *
 * Carryforward: this UI is hidden in production builds before launch.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Zap, Trash2, Play, Plus, RefreshCw, Rewind } from "lucide-react";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SimulatorPanel() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const enabled = process.env.REACT_APP_SIMULATOR_ENABLED === "true" || user?.role === "admin";

  const [leagues, setLeagues] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [teamCount, setTeamCount] = useState(12);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/simulator/admin/leagues`, { headers: authHeaders() });
      setLeagues(r.data || []);
      setError(null);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  useEffect(() => {
    if (!enabled) return;
    // Wait until auth token exists before hitting the API.
    let tries = 0;
    const tryFetch = () => {
      const hasToken = !!localStorage.getItem("pgf_token");
      if (hasToken || tries > 20) {
        refresh();
      } else {
        tries++;
        setTimeout(tryFetch, 200);
      }
    };
    tryFetch();
  }, [enabled]);

  const handleCreate = async () => {
    setBusy("create"); setError(null);
    try {
      const r = await axios.post(`${API}/simulator/create-league`,
        { name: name || undefined, team_count: Number(teamCount) },
        { headers: authHeaders() });
      await refresh();
      setName("");
      // Surface success: show the new league prominently
      navigate(`/simulator?focus=${r.data.league_id}`);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  const handleFastDraft = async (leagueId) => {
    setBusy(`fast:${leagueId}`); setError(null);
    try {
      await axios.post(`${API}/simulator/${leagueId}/fast-complete-draft`, {}, { headers: authHeaders() });
      await refresh();
      navigate(`/league/${leagueId}/home`);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  const handleDelete = async (leagueId) => {
    if (!window.confirm("Delete this league and all season state? Destructive.")) return;
    setBusy(`del:${leagueId}`); setError(null);
    try {
      await axios.delete(`${API}/simulator/${leagueId}`, { headers: authHeaders() });
      await refresh();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  const handleAdvance = async (leagueId) => {
    setBusy(`adv:${leagueId}`); setError(null);
    try {
      await axios.post(`${API}/season/${leagueId}/advance-week`, {}, { headers: authHeaders() });
      await refresh();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  const handleRewind = async (leagueId) => {
    const wkStr = window.prompt("Rewind to which week? (1-17). DESTRUCTIVE: clears state from weeks > target.");
    if (!wkStr) return;
    const wk = parseInt(wkStr, 10);
    if (!wk || wk < 1 || wk > 17) { alert("Invalid week"); return; }
    if (!window.confirm(`Confirm rewind to Week ${wk}? Lineups, scores, and stats from weeks > ${wk} will be permanently deleted.`)) return;
    setBusy(`rw:${leagueId}`); setError(null);
    try {
      await axios.post(`${API}/simulator/${leagueId}/reset-to-week/${wk}`, {}, { headers: authHeaders() });
      await refresh();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  if (!enabled) {
    return (
      <div className="min-h-screen bg-[#111111] text-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <p className="font-display font-black text-3xl uppercase text-[#FF6B00] mb-2">Access Denied</p>
          <p className="text-[#A0A0A0]">Simulator is admin-only.</p>
        </div>
      </div>
    );
  }

  const bg = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  return (
    <div className={`min-h-screen ${bg} ${fg}`} data-testid="simulator-panel">
      {/* DEV BANNER */}
      <div className="bg-[#FF6B00] text-white text-center py-2 text-xs font-display font-black uppercase tracking-widest">
        DEV TOOLS - DO NOT EXPOSE IN PRODUCTION
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mb-2">Simulator</h1>
        <p className={`${muted} font-body mb-8`}>Internal QA tools. Spin up CPU leagues, fast-complete drafts, jump weeks.</p>

        {error && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 px-4 py-3 mb-6 font-body text-sm" data-testid="sim-error">
            {error}
          </div>
        )}

        {/* CREATE */}
        <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-5 mb-8`}>
          <h2 className="font-display font-bold uppercase tracking-wide text-lg mb-3">Create CPU League</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="League name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`flex-1 px-4 py-2 rounded-lg ${isDark ? "bg-[#2A2A2A] text-white" : "bg-[#F0F0F0] text-black"} border ${cardBorder} font-body`}
              data-testid="sim-league-name"
            />
            <select
              value={teamCount}
              onChange={(e) => setTeamCount(e.target.value)}
              className={`px-4 py-2 rounded-lg ${isDark ? "bg-[#2A2A2A] text-white" : "bg-[#F0F0F0] text-black"} border ${cardBorder} font-body`}
              data-testid="sim-team-count"
            >
              <option value={8}>8 teams</option>
              <option value={10}>10 teams</option>
              <option value={12}>12 teams</option>
              <option value={14}>14 teams</option>
            </select>
            <button
              onClick={handleCreate}
              disabled={busy === "create"}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-bold uppercase tracking-wide disabled:opacity-50"
              data-testid="sim-create-btn"
            >
              <Plus size={16} /> Create
            </button>
          </div>
        </div>

        {/* LEAGUES LIST */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold uppercase tracking-wide text-lg">Leagues</h2>
          <button
            onClick={refresh}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${cardBorder} ${muted} hover:${fg} text-sm`}
            data-testid="sim-refresh"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
        <div className="space-y-3">
          {leagues.length === 0 && (
            <div className={`rounded-xl ${cardBg} border ${cardBorder} p-6 text-center ${muted}`}>
              No leagues yet. Create one above.
            </div>
          )}
          {leagues.map((l) => (
            <div key={l.id} className={`rounded-xl ${cardBg} border ${cardBorder} p-4 flex flex-col sm:flex-row sm:items-center gap-3`} data-testid={`sim-league-${l.id}`}>
              <div className="flex-1">
                <div className="font-display font-bold uppercase tracking-wide">{l.name}</div>
                <div className={`text-xs ${muted} mt-0.5`}>
                  {l.team_count} teams · <span className="text-[#FF6B00] font-bold">{l.status}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {(l.status === "SETUP" || l.status === "LOBBY" || l.status === "DRAFTING") && (
                  <button
                    onClick={() => handleFastDraft(l.id)}
                    disabled={busy === `fast:${l.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00C853]/15 border border-[#00C853]/40 text-[#00C853] hover:bg-[#00C853]/25 text-sm font-display font-bold uppercase disabled:opacity-50"
                    data-testid={`sim-fast-${l.id}`}
                  >
                    <Zap size={14} /> Fast-complete draft
                  </button>
                )}
                {(l.status === "DRAFT_COMPLETE" || l.status === "SEASON_ACTIVE") && (
                  <button
                    onClick={() => navigate(`/league/${l.id}/home`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF6B00]/15 border border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/25 text-sm font-display font-bold uppercase"
                    data-testid={`sim-recap-${l.id}`}
                  >
                    <Play size={14} /> Open League
                  </button>
                )}
                {l.status === "SEASON_ACTIVE" && (
                  <>
                    <button
                      onClick={() => handleAdvance(l.id)}
                      disabled={busy === `adv:${l.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00C853]/15 border border-[#00C853]/40 text-[#00C853] hover:bg-[#00C853]/25 text-sm font-display font-bold uppercase disabled:opacity-50"
                      data-testid={`sim-advance-${l.id}`}
                    >
                      <Play size={14} /> Advance Week
                    </button>
                    <button
                      onClick={() => handleRewind(l.id)}
                      disabled={busy === `rw:${l.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FFAA00]/15 border border-[#FFAA00]/40 text-[#FFAA00] hover:bg-[#FFAA00]/25 text-sm font-display font-bold uppercase disabled:opacity-50"
                      data-testid={`sim-rewind-${l.id}`}
                      title="Rewind season to an earlier week (destructive)"
                    >
                      <Rewind size={14} /> Rewind
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDelete(l.id)}
                  disabled={busy === `del:${l.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-sm font-display font-bold uppercase disabled:opacity-50"
                  data-testid={`sim-delete-${l.id}`}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
